import { useEffect, useMemo, useState } from 'react';
import classnames from 'classnames/bind';
import { Button, ChevronRightIcon } from 'tuui';
import { AlertIcon, HelpTooltip, TOOLTIP_POSITION, TooltipV2 as Tooltip } from 'taboola-ultimate-ui';
import { ExternalLink } from 'components';
import { WarningIcon } from 'components/NotificationsCenter/Icons/NoficationsTypesIcons/NotificationTypeIcons';
import { useNavigate } from 'hooks';
import { generateCreativeEditorPath } from 'modules/campaigns/services/utils';
import { GTM_EVENTS, gtmTracker } from 'modules/taboola-common-frontend-modules/gtmTracker';
import { FormattedMessage } from 'modules/taboola-common-frontend-modules/i18n';
import styles from './craTagsTooltip.module.scss';

const classNameBuilder = classnames.bind(styles);

const msgIdPrefix = 'app.campaigns.creative.tags';
const GTM_PREFIX = 'CRA_Tags: ';
const EMPTY_TAGS = {};
const EMPTY_PUBLISHERS = [];
const helpCenterLink = 'https://www.taboola.com/help/en/articles/9922446-limited-supply-access';
const tagToArticleIdMap = {
    APPLE_COMPETITOR_PRODUCT_AND_SERVICE: 'h_6178e00fb2',
    APPLE_BLOCK: 'h_b9c665efee',
    STREAMING_SERVICE: 'h_a30d9ceac3',
    DIGITAL_DEVICE: 'h_6250da17f5',
    INGESTIBLE_CBD: 'h_afd28b589a',
    WEIGHT_LOSS_SUPPLEMENT: 'h_d4b8602e9a',
    CELEBRITY_WEIGHT_LOSS: 'h_a9e6f1089e',
    APPLE_BLOCK_BACKFILL: 'h_38c3a25219',
    APPLE_PRODUCT_AND_SERVICE: 'h_53109b1e4a',
    APPLE_GRAMMAR_UNSAFE: 'h_74522ee005',
    APPLE_DISTASTEFUL_IMAGE: 'h_3cf960cb8d',
    APPLE_GRAINY_IMAGE: 'h_b201a8aac5',
    APPLE_REVIEW_REJECTED: 'h_85e3c49cf8',
    DISTASTEFUL_IMAGE: 'h_3cf960cb8d',
    CLOSE_UP_BODY_PARTS: 'h_3d84814987',
    MATURE_THEMES: 'h_271c71a793',
    AGGRESSIVE_PROMISES: 'h_810f880c71',
};

export const CRATagsTooltip = ({
    tags = EMPTY_TAGS,
    publishers = EMPTY_PUBLISHERS,
    accountName,
    campaignId,
    creativeId,
    initialSelected,
}) => {
    const [selected, setSelected] = useState(initialSelected);
    const navigate = useNavigate();

    useEffect(() => {
        setSelected(initialSelected);
    }, [initialSelected]);

    const onMouseEnter = () => {
        gtmTracker.trackEvent(GTM_EVENTS.USABILITY, {
            component: `${GTM_PREFIX}Tooltip Visible`,
            value: accountName,
        });
    };

    const navigateToAdsEditDrawer = () => {
        navigate(generateCreativeEditorPath({ accountId: accountName, campaignId, creativeId }));
    };

    // map selected tags to messages
    const tagContent = useMemo(
        () =>
            Array.from(tags[selected])
                .sort((a, b) => a.tag.localeCompare(b.tag))
                .map(t => (
                    <ExternalLink
                        key={t.tag}
                        className={styles['link']}
                        href={`${helpCenterLink}#${tagToArticleIdMap[t.tag] || 'h_9df4ca879e'}`}
                    >
                        <FormattedMessage
                            id={`${msgIdPrefix}.content.tag.reason.${t.tag}`}
                            defaultMessage={t.tag}
                            className={styles['reason']}
                        >
                            {message => (
                                <>
                                    {message}
                                    <Tooltip interactive>
                                        <ExternalLink
                                            href={`${helpCenterLink}#${tagToArticleIdMap[t.tag] || 'h_9df4ca879e'}`}
                                        >
                                            {t.comment || (
                                                <FormattedMessage
                                                    id={`${msgIdPrefix}.content.tag.description.${t.tag}`}
                                                    defaultMessage="Please review the help documentation for more details"
                                                />
                                            )}
                                        </ExternalLink>
                                    </Tooltip>
                                </>
                            )}
                        </FormattedMessage>
                    </ExternalLink>
                )),
        [tags, selected]
    );

    return (
        <HelpTooltip
            IconComponent={AlertIcon}
            iconClassName={styles['alert-icon']}
            position={TOOLTIP_POSITION.RIGHT}
            className={styles['tooltip']}
            maskClassName={styles['tooltip-mask']}
            tailClassName={styles['tooltip-tail']}
            tailHeight={9}
            tailStyle={{
                zIndex: 1,
            }}
            onMouseEnter={onMouseEnter}
        >
            <div className={styles['container']}>
                <div className={styles['header']}>
                    <div className={styles['title']}>
                        <WarningIcon />
                        <FormattedMessage id={`${msgIdPrefix}.header.title`} defaultMessage="Limited Supply Access" />
                    </div>
                    <FormattedMessage
                        id={`${msgIdPrefix}.header.description`}
                        defaultMessage="Your ad may face restrictions. If targeting the flagged sites, you may want to resolve the listed issues in order to maximize campaign reach. {suffix} Click below to learn how to fix the issue then click on Edit Ad."
                        values={{
                            suffix: (
                                <FormattedMessage
                                    id={`${msgIdPrefix}.header.description.disclaimer`}
                                    defaultMessage="If not targeting these sites, you can disregard this notice."
                                    className={styles['important']}
                                />
                            ),
                        }}
                        className={styles['header-description']}
                    />
                    <FormattedMessage
                        id={`${msgIdPrefix}.header.disclaimer`}
                        defaultMessage="Note: making changes does not guarantee inventory, as other factors may impact ad delivery."
                        className={classNameBuilder('header-description', 'disclaimer')}
                    />
                </div>
                <div className={styles['content']}>
                    <div className={styles['publishers-container']}>
                        <div className={styles['publishers-header']}>
                            <FormattedMessage
                                id={`${msgIdPrefix}.content.publishers.title`}
                                defaultMessage="Restricted Publishers:"
                                className={styles['content-title']}
                            />
                            <HelpTooltip
                                position={TOOLTIP_POSITION.RIGHT}
                                iconClassName={styles['help-icon']}
                                tailHeight={9}
                                tailStyle={{
                                    zIndex: 1,
                                }}
                                onMouseEnter={onMouseEnter}
                            >
                                <ExternalLink href={`${helpCenterLink}#h_9ba897bf75`}>
                                    <FormattedMessage
                                        id={`${msgIdPrefix}.content.publisher.tooltip.footer`}
                                        defaultMessage="Learn more about Restricted Publishers"
                                    />
                                </ExternalLink>
                            </HelpTooltip>
                        </div>
                        <div className={styles['publishers-list']} data-testid="cra-tags-publishers">
                            {publishers.map(groupId => {
                                return (
                                    <div
                                        key={groupId}
                                        className={classNameBuilder('publisher', {
                                            selected: selected === groupId,
                                        })}
                                        onClick={() => setSelected(groupId)}
                                        data-metrics-event-name={GTM_EVENTS.USABILITY}
                                        data-metrics-component={`${GTM_PREFIX}Affected Publisher Clicked`}
                                        data-metrics-value={groupId}
                                    >
                                        <FormattedMessage
                                            id={`${msgIdPrefix}.content.publisher.${groupId}.label`}
                                            defaultMessage={groupId}
                                        />
                                        <div>
                                            <ChevronRightIcon className={styles['chevron']} />
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                    <div className={styles['divider']} />
                    <div className={styles['reasons-container']}>
                        <FormattedMessage
                            id={`${msgIdPrefix}.content.reasons.title`}
                            defaultMessage="Restricted Content:"
                            className={styles['content-title']}
                        />
                        <div className={styles['reasons-list']} data-testid="cra-tags-reasons">
                            {tagContent}
                        </div>
                    </div>
                </div>
                <div className={styles['footer']}>
                    <ExternalLink className={styles['link']} href={helpCenterLink}>
                        <FormattedMessage
                            id={`${msgIdPrefix}.footer.help.link`}
                            defaultMessage="Learn how to fix it here"
                        />
                    </ExternalLink>
                    <Button onClick={navigateToAdsEditDrawer}>
                        <FormattedMessage id={`${msgIdPrefix}.footer.button`} defaultMessage="Edit Ad" />
                    </Button>
                </div>
            </div>
        </HelpTooltip>
    );
};
